import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Flame, Layers, Sparkles } from "lucide-react";
import { useBusiness } from "@/components/business-context";
import { getDashboard } from "@/lib/analytics.functions";
import { CATEGORIES, todayISO } from "@/lib/momentum-types";

export const Route = createFileRoute("/_authenticated/app/dashboard")({
  head: () => ({
    meta: [
      { title: "Dashboard — The Founders App" },
      { property: "og:title", content: "Dashboard — The Founders App" },
    ],
  }),
  component: DashboardPage,
});

function labelFor(value: string) {
  return CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="soft-panel p-5">
      <p className="micro-label">{label}</p>
      <p className="mt-3 text-3xl font-semibold tabular-nums text-foreground">{value}</p>
      {hint && <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{hint}</p>}
    </div>
  );
}

function DashboardPage() {
  const { active } = useBusiness();
  const localToday = todayISO();
  const q = useQuery({
    queryKey: ["dashboard", active?.id, localToday],
    queryFn: () => getDashboard({ data: { businessId: active!.id, today: localToday } }),
    enabled: Boolean(active?.id),
  });

  const d = q.data;

  return (
    <div>
      <p className="micro-label">Dashboard</p>
      <h1 className="etched mt-4 text-3xl font-semibold sm:text-4xl">
        <span className="notranslate">{active?.name ?? "Your business"}</span> at a glance.
      </h1>
      <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
        Everything below is read from what you have already logged. No extra work — just the shape
        of the last few weeks.
      </p>

      {q.isLoading && <p className="mt-10 text-sm text-muted-foreground">Reading your history…</p>}

      {d && (
        <>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="This week"
              value={`${d.week.completed}/${d.week.logged}`}
              hint={`Moves closed out across ${d.week.activeDays} active ${
                d.week.activeDays === 1 ? "day" : "days"
              }.`}
            />
            <Stat
              label="This month"
              value={`${d.month.completed}/${d.month.logged}`}
              hint="Moves closed out of moves logged this month."
            />
            <Stat
              label="Active days"
              value={`${d.month.activeDays} ${d.month.activeDays === 1 ? "day" : "days"}`}
              hint={
                d.month.activeDays > 0
                  ? "Days this month with something written down. No streaks to keep."
                  : "Nothing logged this month yet. One line is enough."
              }
            />
            <Stat
              label="Journal & outreach"
              value={`${d.journalCount30} / ${d.interactionCount30}`}
              hint="Journal pages and contact interactions in the last 30 days."
            />
          </div>

          <section className="soft-panel mt-6 p-7">
            <p className="micro-label flex items-center gap-2">
              <Layers className="h-3.5 w-3.5" aria-hidden="true" />
              Where your attention went
            </p>
            {d.categories.length === 0 ? (
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                No categories tagged yet this month. Tagging a move is optional, but it is what
                makes this panel useful.
              </p>
            ) : (
              <>
                <div className="mt-5 space-y-2">
                  {d.categories.map((c) => {
                    const max = d.categories[0]?.count || 1;
                    return (
                      <div key={c.name} className="flex items-center gap-3">
                        <span className="w-24 shrink-0 text-xs text-muted-foreground">
                          {labelFor(c.name)}
                        </span>
                        <span className="h-2 flex-1 rounded-sm bg-secondary">
                          <span
                            className="block h-2 rounded-sm bg-primary/70"
                            style={{ width: `${Math.round((c.count / max) * 100)}%` }}
                          />
                        </span>
                        <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                          {c.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                  Most of your moves went to{" "}
                  <span className="text-foreground">{labelFor(d.topCategory!.name)}</span>
                  {d.quietCategory && (
                    <>
                      . The quietest corner is{" "}
                      <span className="text-foreground">{labelFor(d.quietCategory.name)}</span> —
                      worth a look if it matters to you
                    </>
                  )}
                  .
                </p>
              </>
            )}
          </section>

          <section className="soft-panel mt-6 p-7">
            <p className="micro-label flex items-center gap-2">
              <Flame className="h-3.5 w-3.5" aria-hidden="true" />
              Monthly bold move
            </p>
            {d.boldMove ? (
              <>
                <p className="notranslate mt-4 text-sm leading-relaxed text-foreground">{d.boldMove.text}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Set {d.boldMove.daysSince ?? 0} {d.boldMove.daysSince === 1 ? "day" : "days"} ago.
                </p>
              </>
            ) : (
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                No bold move recorded yet. Today will ask for one at the start of the month.
              </p>
            )}
          </section>

          <section className="soft-panel mt-6 p-7">
            <p className="micro-label flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Daily digest
            </p>
            <div className="mt-5 grid gap-6 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Today</p>
                {d.digest.today.length === 0 ? (
                  <p className="mt-3 text-sm text-muted-foreground">Nothing written down yet.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {d.digest.today.map((t, i) => (
                      <li key={i} className="notranslate text-sm leading-relaxed text-foreground">
                        {t.done ? "✓" : "•"} {t.text}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Yesterday
                </p>
                {d.digest.yesterday.length === 0 ? (
                  <p className="mt-3 text-sm text-muted-foreground">Nothing logged.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {d.digest.yesterday.map((t, i) => (
                      <li key={i} className="notranslate text-sm leading-relaxed text-foreground">
                        {t.done ? "✓" : "•"} {t.text}
                        {t.outcome && (
                          <span className="mt-1 block text-xs text-muted-foreground">
                            {t.outcome}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          <section className="soft-panel mt-6 p-7">
            <p className="micro-label flex items-center gap-2">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
              Coming up
            </p>
            {d.upcoming.length === 0 ? (
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Nothing on the calendar for this business.
              </p>
            ) : (
              <ul className="mt-5 space-y-3">
                {d.upcoming.map((a) => (
                  <li key={a.id} className="flex items-baseline justify-between gap-4">
                    <span className="min-w-0">
                      <span className="notranslate block truncate text-sm text-foreground">{a.title}</span>
                      {a.location && (
                        <span className="notranslate block truncate text-xs text-muted-foreground">
                          {a.location}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {new Date(a.starts_at).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
